import { customerService } from '@services/customer.service';
import { ticketService } from '@services/ticket.service';

// 11e's candidate list.
//
// The matcher is the one that already shipped on this screen — phone, exact
// email, similar name — kept verbatim, including the reason it records for each
// hit. That reason IS the evidence the dialog renders: `SAME PHONE NUMBER` is
// why a person believes two records are one person, and a similarity score
// tells them nothing about whether to trust the match.

export interface MergeCandidate {
  id: string;
  email: string;
  name: string | null;
  ticketCount: number;
  activityCount: number;
  /** 'Mar 2024' — pre-formatted for display. */
  since: string;
  organisation: string | null;
  /** 'SAME PHONE NUMBER · SIMILAR NAME' — shown verbatim, never a score. */
  evidence: string;
  isRichest: boolean;
}

interface SeedCustomer {
  id: string;
  email: string;
  name?: string;
  phone?: string;
  createdAt: string;
  organization?: { name: string };
}

/** Two names are the same person if their word sets overlap enough. */
function namesAreSimilar(a?: string, b?: string): boolean {
  if (!a || !b) return false;
  const n1 = a.toLowerCase().trim();
  const n2 = b.toLowerCase().trim();
  if (n1 === n2) return true;
  const p1 = n1.split(/\s+/);
  const p2 = n2.split(/\s+/);
  const matching = p1.filter((p) => p2.includes(p));
  return matching.length >= Math.min(p1.length, p2.length) * 0.5;
}

function monthYear(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

/** Tickets and activities for one record — what `isRichest` is ranked on. */
async function weigh(id: string): Promise<{ tickets: number; activities: number }> {
  const [tickets, activities] = await Promise.all([
    ticketService
      .getTickets({ customerId: id, page: 1, limit: 1 })
      .then((r) => r.total ?? 0)
      .catch(() => 0),
    customerService
      .getTimeline(id, { limit: 1 })
      .then((r) => r.total ?? 0)
      .catch(() => 0),
  ]);
  return { tickets, activities };
}

/**
 * Every record that looks like the same person as `seed`, seed included.
 *
 * The seed is always in the list because it is one of the records being merged
 * — 11e asks which of N survives, not whether to fold others into the one you
 * happen to have open.
 */
export async function findMergeCandidates(seed: SeedCustomer): Promise<MergeCandidate[]> {
  const found = new Map<string, { c: any; reasons: string[] }>();
  const add = (c: any, reason: string) => {
    if (!c || c.id === seed.id || c.status === 'merged') return;
    const existing = found.get(c.id);
    if (existing) {
      if (!existing.reasons.includes(reason)) existing.reasons.push(reason);
    } else {
      found.set(c.id, { c, reasons: [reason] });
    }
  };

  // Search by the LAST 4 digits, not the full number: the search endpoint is a
  // raw substring filter, so another record stored with separators
  // ("555-123-4567") would never contain "5551234567". The exact last-10
  // comparison below filters the hits.
  const phoneDigits = (seed.phone || '').replace(/\D/g, '').slice(-10);
  const name = (seed.name || '').trim();
  const email = (seed.email || '').trim().toLowerCase();

  if (phoneDigits) {
    const res = await customerService.getCustomers({ search: phoneDigits.slice(-4), limit: 50 });
    (res.data || []).forEach((c: any) => {
      if ((c.phone || '').replace(/\D/g, '').slice(-10) === phoneDigits) add(c, 'SAME PHONE');
    });
  }

  if (email) {
    const res = await customerService.getCustomers({ search: email, limit: 50 });
    (res.data || []).forEach((c: any) => {
      if ((c.email || '').trim().toLowerCase() === email) add(c, 'SAME EMAIL');
    });
  }

  if (name) {
    const res = await customerService.getCustomers({ search: name, limit: 50 });
    (res.data || []).forEach((c: any) => {
      if (namesAreSimilar(name, c.name)) add(c, 'SIMILAR NAME');
    });
  }

  if (found.size === 0) return [];

  const rows = [
    { c: seed as any, reasons: ['THIS RECORD'] },
    ...found.values(),
  ];

  const weighed = await Promise.all(
    rows.map(async (r) => {
      const w = await weigh(r.c.id);
      return {
        id: r.c.id,
        email: r.c.email,
        name: r.c.name ?? null,
        ticketCount: w.tickets,
        activityCount: w.activities,
        since: monthYear(r.c.createdAt),
        organisation: r.c.organization?.name ?? null,
        evidence: r.reasons.join(' · '),
        isRichest: false,
        _createdAt: r.c.createdAt,
      };
    }),
  );

  // Richest = most history, tie-broken on the oldest record. The default
  // primary must be the one that loses the least if it were merged away.
  let best = 0;
  weighed.forEach((r, i) => {
    const a = r.ticketCount + r.activityCount;
    const b = weighed[best].ticketCount + weighed[best].activityCount;
    if (a > b || (a === b && new Date(r._createdAt) < new Date(weighed[best]._createdAt))) best = i;
  });
  weighed[best].isRichest = true;

  return weighed.map(({ _createdAt, ...rest }) => rest);
}
